import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { logoutFromHelper } from "api/Helper";
import logoutIcon from "assets/images/logout.svg";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { useMyConsultationsList } from "features/consultations/hooks/useConsultationsList";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { pathGenerators } from "utils/routes";

import { queryClient } from "../../../../layouts/GlobalLayout";
import { logout } from "../../../../redux/auth/AuthSlice";
import { removeuser } from "../../../../redux/user/userSlice";
import styles from "./LogoutButton.module.css";

/**
 * Logout trigger and its confirmation.
 *
 * The caller supplies the trigger's look — the sidebar renders an icon with a
 * tooltip, the profile dropdown renders a row — while this component owns the
 * dialog and the logout itself. Previously the dialog lived in the header's
 * profile dropdown and the sidebar button reached it by Bootstrap target id,
 * so the sidebar's logout silently depended on the header being mounted.
 */
export const LogoutButton = ({
  className = "",
  children,
  onTriggerClick,
  ariaLabel = "Logout",
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: practiceSettings } = usePracticeSettings();
  const { data: openConsultations } = useMyConsultationsList();

  const openConsultationList =
    practiceSettings?.showOpenConsultationsOnLogout !== false
      ? openConsultations ?? []
      : [];

  const handleLogout = () => {
    logoutFromHelper();
    dispatch(logout());
    dispatch(removeuser());
    // Same-tab identity switch (e.g. two staff testing back-to-back) would
    // otherwise leave the previous user's cached queries in memory for the
    // next login to read — see useStaffInfo.
    queryClient.clear();
    navigate(pathGenerators.auth.logout());
  };

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={ariaLabel}
        onClick={() => {
          onTriggerClick?.();
          setIsConfirmOpen(true);
        }}
      >
        {children}
      </button>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleLogout}
        title="Log out"
        confirmLabel="Log out"
        message={
          <div className={styles.body}>
            <span className={styles.iconFrame}>
              <img src={logoutIcon} alt="" className={styles.icon} />
            </span>
            <p className={styles.prompt}>Are you sure you want to log out?</p>

            {openConsultationList.length > 0 && (
              <div className={styles.warning}>
                <p className={styles.warningTitle}>
                  You have {openConsultationList.length} open consultation
                  {openConsultationList.length > 1 ? "s" : ""}
                </p>
                <ul className={styles.warningList}>
                  {openConsultationList.map((consultation) => (
                    <li key={consultation.id}>
                      <strong>
                        {consultation.patient?.firstName}{" "}
                        {consultation.patient?.lastName}
                      </strong>
                      {!!consultation.startTime && (
                        <>
                          {" — started "}
                          {moment(consultation.startTime).format(
                            "DD/MM/YYYY HH:mm"
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        }
      />
    </>
  );
};
